// Thin wrapper around the Cache API for storing binary blobs
// (images, mp3s) keyed by storagePath. Cache API is used instead of
// IndexedDB for blobs because it streams better and survives longer
// under quota pressure.

function makeRequestKey(storagePath: string): Request {
  // Use a synthetic URL so entries are addressable and reviewable in
  // DevTools (Cache Storage panel). The host is irrelevant — only the
  // full URL must be stable and unique per path.
  return new Request(`https://offline-cache.local/${encodeURIComponent(storagePath)}`)
}

async function openCache(cacheName: string): Promise<Cache | null> {
  if (typeof caches === 'undefined') return null
  try {
    return await caches.open(cacheName)
  } catch (err) {
    console.warn('[offline] caches.open failed:', err)
    return null
  }
}

export async function cacheBlob(
  cacheName: string,
  storagePath: string,
  blob: Blob,
): Promise<boolean> {
  const cache = await openCache(cacheName)
  if (!cache) return false

  try {
    const response = new Response(blob, {
      headers: {
        'Content-Type': blob.type || 'application/octet-stream',
        'Content-Length': String(blob.size),
      },
    })
    await cache.put(makeRequestKey(storagePath), response)
    return true
  } catch (err) {
    if ((err as DOMException)?.name === 'QuotaExceededError') {
      console.warn('[offline] Quota exceeded while caching', storagePath)
    } else {
      console.warn('[offline] Failed to cache blob:', err)
    }
    return false
  }
}

export async function getBlob(
  cacheName: string,
  storagePath: string,
): Promise<Blob | null> {
  const cache = await openCache(cacheName)
  if (!cache) return null

  try {
    const response = await cache.match(makeRequestKey(storagePath))
    if (!response) return null
    return await response.blob()
  } catch (err) {
    console.warn('[offline] Failed to read cached blob:', err)
    return null
  }
}

export async function deleteBlob(
  cacheName: string,
  storagePath: string,
): Promise<boolean> {
  const cache = await openCache(cacheName)
  if (!cache) return false

  try {
    return await cache.delete(makeRequestKey(storagePath))
  } catch (err) {
    console.warn('[offline] Failed to delete cached blob:', err)
    return false
  }
}

export async function listCachedPaths(cacheName: string): Promise<string[]> {
  const cache = await openCache(cacheName)
  if (!cache) return []

  try {
    const requests = await cache.keys()
    return requests.map((req) => {
      const url = new URL(req.url)
      return decodeURIComponent(url.pathname.slice(1))
    })
  } catch (err) {
    console.warn('[offline] Failed to list cached paths:', err)
    return []
  }
}

// Approximate size of a named cache in bytes. Walks every entry and
// reads the Content-Length header; falls back to blob().size if
// missing. Can be expensive for large caches — call sparingly.
export async function getCacheSize(cacheName: string): Promise<number> {
  const cache = await openCache(cacheName)
  if (!cache) return 0

  try {
    const requests = await cache.keys()
    let total = 0

    for (const req of requests) {
      const res = await cache.match(req)
      if (!res) continue

      const headerSize = res.headers.get('Content-Length')
      if (headerSize) {
        total += Number(headerSize) || 0
        continue
      }

      const blob = await res.clone().blob()
      total += blob.size
    }

    return total
  } catch (err) {
    console.warn('[offline] Failed to size cache:', err)
    return 0
  }
}
